import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { fetchGalleryBySlug, getOptimizedImageUrl } from '../lib/cms';
import { Shield, Flame, Anchor, AlertTriangle, Check, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

export default function Marine({ isEn }) {
  const [gallery, setGallery] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadGallery() {
      try {
        const data = await fetchGalleryBySlug('marine');
        setGallery(data);
      } catch (error) {
        console.error('Error loading marine gallery:', error);
      } finally {
        setLoading(false);
      }
    }
    loadGallery();
  }, []);

  const lang = isEn ? 'en' : 'da';

  const seoTitle = isEn 
    ? 'Marine Insulation - Fredericia Isolerings-puder' 
    : 'Marineisolering - Fredericia Isolerings-puder';
  const seoDesc = isEn 
    ? 'Marine approved insulation pads for ships, offshore platforms, and engine rooms. Protect equipment and prevent engine fires.'
    : 'Marinegodkendte isoleringspuder til skibe, offshore platforme og maskinrum. Beskyt udstyr og forebyg motorbrand.';

  const points = isEn ? [
    'Marine-approved products specifically designed for harsh maritime environments.',
    'Highly effective personal protection against extreme engine temperatures.',
    'Prevents engine fire by shielding very hot engine parts and turbos.',
    'Moisture and salt-resistant materials ensure long-term durability at sea.',
    'Tailored solutions for pipes, engines, boilers, and exhaust systems.',
    '100% produced with green energy from our own solar plant.'
  ] : [
    'Marinegodkendte produkter specielt designet til barske maritime miljøer.',
    'Særdeles effektiv personlig beskyttelse mod ekstreme motortemperaturer.',
    'Modvirker motorbrand ved at afskærme meget varme motordele og turboer.',
    'Fugt- og saltbestandige materialer sikrer langvarig holdbarhed til søs.',
    'Skræddersyede løsninger til rør, motorer, kedler og udstødningssystemer.',
    '100% produceret med grøn strøm fra vores eget solcelleanlæg.'
  ];

  return (
    <>
      <Helmet>
        <title>{seoTitle}</title>
        <meta name="description" content={seoDesc} />
      </Helmet>

      {/* Hero Section */}
      <section className="relative py-20 bg-brand-dark overflow-hidden border-b border-gray-800">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#1f2937_1px,transparent_1px),linear-gradient(to_bottom,#1f2937_1px,transparent_1px)] bg-[size:5rem_5rem] opacity-10" />
        <div className="absolute right-0 top-0 h-full w-1/3 bg-brand-accent transform -skew-x-12 translate-x-12" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="w-full text-left space-y-4">
            <h1 className="text-4xl sm:text-5xl font-display font-bold text-white tracking-tight">
              {isEn ? 'Marine Insulation' : 'Marineisolering'}
            </h1>
          </div>
        </div>
      </section>

      {/* Main Content & Benefits */}
      <section className="py-20 bg-gray-50 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 space-y-6 text-left">
            <h2 className="text-2xl sm:text-3xl font-display font-bold text-gray-900 tracking-tight">
              {isEn ? 'Effective Protection & Safety at Sea' : 'Effektiv beskyttelse & sikkerhed til søs'}
            </h2>
            <p className="text-gray-600 text-sm sm:text-base leading-relaxed">
              {isEn 
                ? 'Marine insulation pads are designed to protect equipment and installations from extreme temperatures and the harsh conditions found in maritime environments. Whether on ships, offshore platforms, or other maritime structures, our insulation pads offer an effective solution to minimize heat loss and provide personal protection from very hot engine parts/equipment, thereby also preventing engine fire.'
                : 'Marine isoleringspuder er designet til at beskytte udstyr og installationer mod ekstreme temperaturer og de barske forhold, der findes i maritime miljøer. Uanset om det drejer sig om skibe, offshore-platforme eller andre maritime strukturer, tilbyder vores isoleringspuder en effektiv løsning til at minimere varmetab personlig beskyttelse fra meget varme motordele/udstyr og deraf også modvirke motorbrand.'}
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4">
              {points.map((point, idx) => (
                <div key={idx} className="flex gap-3 items-start">
                  <Check className="h-5 w-5 text-black shrink-0 mt-0.5" />
                  <span className="text-sm text-gray-700 leading-relaxed">{point}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="relative aspect-[4/3] overflow-hidden">
              <img
                src={getOptimizedImageUrl('https://assets.auxodev.dk/assets/f9d0caaf-7221-46f9-967b-59f1f96503ae/marine-custom.jpg', { width: 600 })}
                alt="Marineisolering"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Dynamisk Galleri via CMS-reference */}
      <section className="py-20 bg-brand-dark border-b border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-2xl sm:text-3xl font-display font-bold text-white tracking-tight">
              {isEn ? 'Marine Installation Gallery' : 'Maritimt installationsgalleri'}
            </h2>
            <p className="text-gray-400 text-sm mt-2">
              {isEn 
                ? 'Explore our high-quality marine insulation installations. Automatically updated from our CMS.'
                : 'Udforsk vores maritime isoleringsinstallationer af høj kvalitet. Opdateres automatisk fra vores CMS.'}
            </p>
          </div>

          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-accent" />
            </div>
          ) : gallery?.images && gallery.images.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {gallery.images.map((imgUrl, idx) => (
                <div 
                  key={idx} 
                  className="relative aspect-square rounded-lg overflow-hidden border border-gray-800 bg-brand-surface group hover:border-gray-700 transition-all duration-300"
                >
                  <img
                    src={getOptimizedImageUrl(imgUrl, { width: 400, height: 400, fit: 'cover' })}
                    alt={`Marineisolering ${idx + 1}`}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 text-gray-500 font-mono text-xs">
              {isEn ? 'No gallery images found.' : 'Ingen galleribilleder fundet.'}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-brand-surface relative overflow-hidden">
        <div className="max-w-7xl mx-auto text-center px-4 sm:px-6 lg:px-8 space-y-6 relative z-10">
          <h2 className="text-3xl font-display font-bold text-white tracking-tight">
            {isEn ? 'Need Marine-Approved Insulation?' : 'Har du brug for marinegodkendt isolering?'}
          </h2>
          <p className="text-gray-400 text-sm sm:text-base max-w-2xl mx-auto leading-relaxed">
            {isEn 
              ? 'We offer customized solutions to suit your specific maritime needs. Contact us today by phone, email, or use the contact form.'
              : 'Vi tilbyder skræddersyede løsninger, der passer til dine specifikke maritime behov. Kontakt os i dag via telefon, e-mail eller kontaktformularen.'}
          </p>
          <div className="pt-2">
            <Link
              to={isEn ? '/en/contact' : '/kontakt'}
              className="inline-flex items-center gap-2 bg-brand-accent hover:bg-brand-accent-hover text-white font-medium px-6 py-3.5 rounded transition-colors shadow-lg shadow-brand-accent/10"
            >
              {isEn ? 'Contact Our Team' : 'Kontakt vores team'}
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
